// frontend/src/components/common/DisabledReason.jsx
import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

/**
 * DisabledReason — a disabled control that says WHY it is disabled.
 *
 * @description
 *   Wraps a control whose `disabled` expression has more than one cause and
 *   shows the cause that actually applies. With `reason` empty/null the child
 *   renders UNCHANGED — no Tooltip, no holder, no extra DOM. With a `reason` the
 *   child sits in an `inline-flex` holder that a MUI `Tooltip` hangs the reason
 *   on, and that is keyboard-reachable: a disabled button fires no pointer
 *   events and cannot be focused, so the tooltip would otherwise be
 *   mouse-only. The holder carries `aria-describedby` onto a visually hidden
 *   copy of the reason that sits NEXT TO the holder, not inside it — the reason
 *   is thereby reachable at the focus point without being read twice. What is
 *   pinned is the attribute chain and the tooltip at hover and at keyboard
 *   focus; the exact announcement of a screen reader is NOT measured.
 *
 *   🔴 The OUTER box (the tooltip anchor) is `position: relative`. That adds no
 *   offset, so nothing moves; it exists so the absolutely positioned hidden
 *   copy is contained by a box INSIDE a scrolling ancestor. Without it the
 *   copy's containing block can sit outside a horizontal scroll container
 *   (`overflowX: 'auto'`), which then does not clip it — it is laid out past
 *   the last column and counts toward the document width (measured on the
 *   position table of `pages/invoicing/Betrieb.jsx`, 2026-11-01). The inner
 *   holder is unchanged; the copy is its SIBLING, so only the outer box needs
 *   the anchor.
 *
 *   🔴 It exists for the buttons whose `disabled` is COMPOSED
 *   (`busy || !topCandidate`). A button disabled by one obvious cause explains
 *   itself; one disabled by two can mean either, and the reader cannot tell
 *   which. The rule is the same one the pattern library already carries for
 *   tooltips: a tooltip carries ADDITIONAL information, never the only path to
 *   it. A plain "while it saves" state therefore gets NO reason — the button is
 *   visibly busy and a tooltip there is noise.
 *
 *   🔴 `reason` presupposes that the child is disabled; the caller derives the
 *   reason from a subset of its `disabled` expression. Nothing here enforces
 *   that — a reason handed in for an ENABLED child yields a double tab stop and
 *   a tooltip on an active control, which `@dontUseFor` forbids.
 *
 * @useWhen
 *   - `disabled={a || b}` where `a` and `b` are two DIFFERENT real causes
 *   - The cause is not visible on screen and the user has to act to clear it
 *   - A form action that stays disabled until a required field is filled
 *
 * @dontUseFor
 *   - A single, self-explaining cause — while loading/saving/busy the button
 *     already says so; use no wrapper at all
 *   - A `disabled` prop passed down from the caller (`disabled || loading`) —
 *     the wrapper cannot name a cause it does not own
 *   - A permanent disabled state that no user action can clear (use a plain
 *     `disabled` plus visible copy near the control)
 *   - A cause that is already VISIBLE next to the control — give that sentence
 *     an `id` and point the disabled control at it with `aria-describedby`;
 *     a wrapped second copy is read twice (OpenCases, 2026-10-09; Probe
 *     `pages/secondLife/OpenCases.grund.test.jsx`)
 *   - An enabled control — `reason` implies the child is disabled; an enabled
 *     button with a tooltip uses MUI's Tooltip directly
 *
 * @related
 *   - RefreshButton / ProgressButton — carry their own busy state, need no reason
 *   - EmptyState — the same "name the reason" discipline for empty surfaces
 *
 * @reuseHints
 *   - Any submit/confirm button gated on `busy || !<required field>`
 *   - Wizard "Next" steps gated on several independent completeness checks
 *
 * @props
 *   - reason — the sentence naming the cause that applies, or empty for none
 *   - children — the disabled control (must be a single element)
 */
export default function DisabledReason({ reason, children }) {
  // Hook runs unconditionally (rules of hooks) — the early return is below it.
  const reasonId = React.useId();

  if (!reason) {
    // No reason: the child is handed back untouched. No Tooltip, no holder —
    // a wrapper here would add a DOM node and a focus stop for nothing.
    return children;
  }

  // A `fullWidth` child expects the full row; an inline-flex holder would
  // collapse it. Everything else stays inline so the holder adds no layout.
  const fullWidth = React.isValidElement(children) && children.props.fullWidth === true;

  // Der Halter ist ein `div`, nicht der Knopf. Ein deaktivierter Knopf feuert
  // keine Zeigerereignisse und ist nicht fokussierbar; ohne Halter waere der
  // Tooltip maus-only. `tabIndex={0}` macht ihn per Tastatur erreichbar.
  // 🔴 Der Grund steht AUSSERHALB des fokussierbaren Halters (Geschwister, nicht
  // Kind): so haengt er ueber `aria-describedby` am Halter, ohne von ihm
  // mitgelesen zu werden. `describeChild` verhindert, dass MUI den Grund als
  // `aria-label` setzt und den Namen des Knopfes ueberschreibt.
  const holderSx = { display: 'inline-flex', ...(fullWidth ? { width: '100%' } : null) };

  // 🔴 Der aeussere Kasten ist `position: relative`, und das ist kein Layout:
  // er traegt keine Versaetze, also bewegt sich nichts. Er wird der
  // BEZUGSBLOCK der versteckten Kopie (`visuallyHidden` = `position: absolute`).
  // Ohne ihn ist der Bezugsblock irgendein Vorfahr — steht der Baustein in
  // einer Scrollbox (`overflowX: 'auto'`, z. B. die Positionstabelle in
  // `pages/invoicing/Betrieb.jsx`), liegt der Bezugsblock AUSSERHALB davon, und
  // die Scrollbox schneidet die absolut gesetzte Kopie nicht. Sie steht dann
  // rechts neben der letzten Spalte und zaehlt zur Dokumentbreite: die
  // Schmalspur-Probe von S9 (a) (2026-11-01) mass den Ueberlauf, obwohl im
  // sichtbaren Bereich nichts abgeschnitten war. Mit dem Bezugsblock INNERHALB
  // der Scrollbox wird sie mitgeschnitten. Der innere Halter bleibt unberuehrt.
  const ankerSx = { ...holderSx, position: 'relative' };

  return (
    <Tooltip title={reason} describeChild>
      {/* Der aeussere `div` ist der Anker des Tooltips — MUI verlangt genau EIN
          Kind; er traegt keinen Fokuspunkt und keine Beschreibung. */}
      <Box component="div" sx={ankerSx}>
        <Box
          component="div"
          data-testid="disabled-reason-holder"
          tabIndex={0}
          aria-describedby={reasonId}
          sx={holderSx}
        >
          {children}
        </Box>
        <Box component="span" id={reasonId} sx={visuallyHidden}>
          {reason}
        </Box>
      </Box>
    </Tooltip>
  );
}
